import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { Plus, Search, Mail, Phone, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", company_id: "none", position: "", email: "", phone: "" };
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");

function ContactDialog({ open, onOpenChange, initial, companies, onSaved }) {
  const [form, setForm] = useState(initial ? { ...initial, company_id: initial.company_id || "none" } : EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, position: form.position, email: form.email, phone: form.phone, company_id: form.company_id === "none" ? null : form.company_id };
      if (editing) await api.patch(`/contacts/${initial.id}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editing ? "Contacto actualizado" : "Contacto creado");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Editar contacto" : "Nuevo contacto"}</DialogTitle><DialogDescription className="sr-only">Formulario de contacto</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" /></div>
          <div className="space-y-1.5">
            <Label>Empresa</Label>
            <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
              <SelectTrigger data-testid="contact-company-select"><SelectValue placeholder="Sin empresa" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin empresa</SelectItem>
                {(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Cargo</Label><Input value={form.position} onChange={(e) => set("position", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="contact-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Contactos() {
  const { can } = useAuth();
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Vista "solo búsqueda": no se cargan todos los contactos al entrar.
  const term = search.trim();
  const active = term.length >= 2;

  const load = useCallback(async () => {
    if (!active) { setContacts(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/contacts", { params: { search: term } });
      setContacts(r.data);
    } catch { setContacts([]); }
    finally { setLoading(false); }
  }, [term, active]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  // Carga perezosa de empresas SOLO al abrir el diálogo (para el selector).
  const openDialog = async (contact) => {
    setEditing(contact);
    setDialogOpen(true);
    if (companies.length === 0) {
      try { const r = await api.get("/companies"); setCompanies(r.data || []); } catch { /* noop */ }
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Contacto eliminado"); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Contactos" subtitle="Busca personas vinculadas a tus clientes y prospectos">
        {can("crm", "create") && <Button size="sm" className="gap-1.5" onClick={() => openDialog(null)} data-testid="new-contact-btn"><Plus className="h-4 w-4" /> Nuevo contacto</Button>}
      </PageHeader>

      <div className="relative w-full sm:max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar contacto por nombre…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="contact-search" autoFocus />
      </div>

      {!active ? (
        <Card className="border-dashed border-border shadow-none py-20 flex flex-col items-center justify-center text-center gap-3" data-testid="contact-search-empty">
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Search className="h-6 w-6" /></div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Busca un contacto</p>
            <p className="text-xs text-muted-foreground max-w-xs">Escribe al menos 2 letras del nombre para ver resultados. No se cargan todos los contactos de golpe.</p>
          </div>
        </Card>
      ) : (
        <Card className="border-border shadow-none overflow-hidden">
          {loading ? <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
            : (contacts || []).length === 0 ? <div className="py-16 text-center text-sm text-muted-foreground" data-testid="contact-no-results">No se encontraron contactos para «{term}».</div>
            : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Nombre</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                    <TableCell className="pl-5">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarFallback className="text-[11px] bg-muted">{initials(c.name)}</AvatarFallback></Avatar>
                        <span className="font-medium text-sm">{c.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.company_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.position || "—"}</TableCell>
                    <TableCell>{c.email ? <a href={`mailto:${c.email}`} className="text-sm text-primary inline-flex items-center gap-1.5 hover:underline"><Mail className="h-3.5 w-3.5" />{c.email}</a> : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground tabular-nums">{c.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.phone}</span> : "—"}</TableCell>
                    <TableCell>
                      {(can("crm", "edit") || can("crm", "delete")) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`contact-menu-${c.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {can("crm", "edit") && <DropdownMenuItem onClick={() => openDialog(c)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>}
                            {can("crm", "delete") && <DropdownMenuItem onClick={() => del(c.id)} className="text-destructive focus:text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Eliminar</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {dialogOpen && <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} companies={companies} onSaved={load} />}
    </div>
  );
}
